import React, { useCallback, useState, useRef } from 'react';

import { Popper } from '@material-ui/core';
import { StyledAvatar } from 'components/avatars';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { StyledBox, StyledGrid, StyledPaper, StyledTooltip, StyledTypography } from 'styles/common';

import { getProfileRoute } from 'constants/helper';
import { MIcon, PlaceIcon, SpeakerIcon, TeamHubHeaderIcon, UserIcon2 } from 'constants/icons';
import theme from 'constants/theme';

import { ActionIconContainer } from '../styles';

export const EmployeeHoverContainer = ({ children, hoverComponent, clickable = true }) => {
  const [pointerLocation, setPointerLocation] = useState({
    elem: false,
    hover: false,
  });

  const componentElem = useRef(null);

  const handleMouseEnter = useCallback(() => {
    setPointerLocation((prev) => ({ ...prev, elem: true }));
  }, []);

  const handleMouseLeave = useCallback(() => {
    setPointerLocation((prev) => ({ ...prev, elem: false }));
  }, []);

  const handleHoverMouseEnter = useCallback(() => {
    setPointerLocation((prev) => ({ ...prev, hover: true }));
  }, []);

  const handleHoverMouseLeave = useCallback(() => {
    setPointerLocation((prev) => ({ ...prev, hover: false }));
  }, []);

  const childrenWithProps = React.Children.map(children, (child, index) =>
    React.cloneElement(child, {
      ref: componentElem,
    })
  );

  return (
    <>
      <StyledEmployeeHoverContainer
        clickable={clickable}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        active={pointerLocation.elem || pointerLocation.hover}
      >
        {childrenWithProps}
      </StyledEmployeeHoverContainer>
      <Popper
        open={pointerLocation.elem || pointerLocation.hover}
        placement="right"
        anchorEl={componentElem.current}
        transition
        style={{ zIndex: 5 }}
        modifiers={{
          offset: {
            enabled: true,
            offset: '0,20',
          },
        }}
      >
        <StyledPaper elevation={0} onMouseEnter={handleHoverMouseEnter} onMouseLeave={handleHoverMouseLeave}>
          {hoverComponent}
        </StyledPaper>
      </Popper>
    </>
  );
};

const IconAndTextComp = ({ icon, text }) => (
  <StyledGrid container item xs="6" wrap="nowrap" alignItems="center">
    {icon}
    <StyledTypography color="textSecondary" variant="body2" leftGutter={1} truncate>
      {text || 'N/A'}
    </StyledTypography>
  </StyledGrid>
);

export default ({
  uuid,
  imageUrl,
  employeeName = '',
  managerName,
  designation,
  department,
  location,
  actionButtonIcon = null,
}) => {
  const history = useHistory();
  const goToProfilePage = () => {
    history.push(getProfileRoute(uuid));
  };

  return (
    <Container>
      <StyledGrid container wrap="nowrap">
        <StyledGrid item>
          <StyledAvatar src={imageUrl} size="medium" variant="circular" />
        </StyledGrid>
        <StyledGrid container leftGutter={2} style={{ width: '40ch' }}>
          <StyledGrid container justify="space-between" alignItems="center" wrap="nowrap" bottomGutter={2}>
            <StyledTypography variant="h6" weight={600} rightGutter={1} truncate>
              {employeeName}
            </StyledTypography>
            <StyledBox marginLeft="auto" display="flex" alignItems="center" justifyContent="center">
              <StyledTooltip title="View profile">
                <ActionIconContainer rightGutter={2} onClick={goToProfilePage}>
                  <UserIcon2 size="tiniest" />
                </ActionIconContainer>
              </StyledTooltip>
              {actionButtonIcon || null}
            </StyledBox>
          </StyledGrid>

          <StyledGrid container wrap="nowrap" bottomGutter={1} alignItems="center">
            <IconAndTextComp text={managerName} icon={<MIcon color={theme.palette.icons.grey2} size="header" />} />
            <IconAndTextComp
              text={designation}
              icon={<SpeakerIcon color={theme.palette.icons.grey2} size="header" />}
            />
          </StyledGrid>
          <StyledGrid container wrap="nowrap" bottomGutter={1} alignItems="center">
            <IconAndTextComp
              text={department}
              icon={<TeamHubHeaderIcon color={theme.palette.icons.grey2} size="header" />}
            />
            <IconAndTextComp text={location} icon={<PlaceIcon color={theme.palette.icons.grey2} size="header" />} />
          </StyledGrid>
        </StyledGrid>
      </StyledGrid>
    </Container>
  );
};

const Container = styled.div`
  background-color: white;
  padding: 1rem;
  box-shadow: 2px 2px 5px ${theme.palette.shadows.popper};
  border: 1px solid ${theme.palette.borders.lighterGrey};
  border-radius: 4px;
  &:after {
    content: ' ';

    background-color: white;

    display: block;
    height: 20px;
    width: 20px;

    position: absolute;
    left: -10px;
    top: 50%;
    z-index: -1;

    transform: rotate(45deg) translateY(-50%);

    box-shadow:
      0px 2px 1px -1px rgb(0 0 0 / 20%),
      0px 1px 1px 0px rgb(0 0 0 / 14%),
      0px 1px 3px 0px rgb(0 0 0 / 12%);
  }
`;

const StyledEmployeeHoverContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;

  display: flex;
  align-items: center;

  &:hover {
    cursor: ${(props) => (props.clickable ? 'pointer' : 'inherit')};
    background-color: ${(props) => props.theme.palette.background.lightBlue};
  }

  ${(props) =>
    props.active &&
    css`
      background-color: ${props.theme.palette.background.lightBlue};
    `};
`;
